import { identity, omit } from 'lodash-es';
import { PODCAST_SERVICE_BASEURL } from 'config';
import ServiceFactory, { returnVoid } from 'services/ServiceFactory';
import {
  EPISODE_SEARCH_PATH,
  FAVORITE_PODCASTS_PATH,
  FAVORITE_PODCAST_PATH,
  FEED_SEARCH_PATH,
  OWNED_PODCASTS_PATH,
  PODCAST_CONFIGURATION_PATH,
  PODCAST_DETAIL_PATH,
  PODCAST_SEARCH_PATH,
  PODPRESS_JOB_DETAIL_PATH,
  PODPRESS_JOB_PATH,
  REFRESH_PODCAST_JOB_STATUS_PATH,
  REFRESH_PODCAST_PATH,
} from './constants';
import {
  AddFavoritePodcastArgs,
  GetPodcastDetailArgs,
  Podcast,
  PodcastDetail,
  PodcastSearchArgs,
  PodcastSearchResults,
  PodcastEpisodeSearchArgs,
  PodcastEpisodeSearchResults,
  FavoritePodcastsResults,
  RemoveFavoritePodcastArgs,
  RefreshPodcastArgs,
  RefreshPodcastResult,
  GetPodcastRefreshStatusArgs,
  GetOwnedPodcastsResult,
  SubmitPodpressJobArgs,
  GetPodpressJobDetailArgs,
  PodpressJobCreationResult,
  PodpressJobDetail,
  GetPodcastConfigArgs,
  PodcastConfiguration,
} from './types';

const serviceFactory = new ServiceFactory(PODCAST_SERVICE_BASEURL);

export const searchForPodcasts = serviceFactory.createGetEndpoint(
  PODCAST_SEARCH_PATH,
)<PodcastSearchArgs, PodcastSearchResults>({
  queryParams: identity,
});

export const searchForPodcastEpisodes = serviceFactory.createGetEndpoint(
  EPISODE_SEARCH_PATH,
)<PodcastEpisodeSearchArgs, PodcastEpisodeSearchResults>({
  queryParams: identity,
});

/**
 * When a podcast cannot be found for the given rssUrl (or if there's any other
 * kind of error, e.g. the rssUrl is not a URL) the backend throws a 500 error
 */
export const searchForPodcastFeed = serviceFactory.createGetEndpoint(
  FEED_SEARCH_PATH,
)<string, Podcast>({
  queryParams: (rssUrl) => ({ url: rssUrl }),
});

export const getFavoritePodcasts = serviceFactory.createGetEndpoint(
  FAVORITE_PODCASTS_PATH,
)<undefined, FavoritePodcastsResults>();

export const addFavoritePodcast = serviceFactory.createPostEndpoint(
  FAVORITE_PODCAST_PATH,
)({
  data: (args: AddFavoritePodcastArgs) => omit(args, 'podcastId'),
  select: returnVoid(),
});

export const removeFavoritePodcast = serviceFactory.createDeleteEndpoint(
  FAVORITE_PODCAST_PATH,
)<RemoveFavoritePodcastArgs, void>({
  select: returnVoid(),
});

export const getPodcastDetail = serviceFactory.createGetEndpoint(
  PODCAST_DETAIL_PATH,
)<GetPodcastDetailArgs, PodcastDetail>({
  queryParams: (args) => omit(args, 'podcastId'),
});

export const refreshPodcast = serviceFactory.createPostEndpoint(
  REFRESH_PODCAST_PATH,
)<RefreshPodcastArgs, RefreshPodcastResult>({
  data: identity,
});

export const getPodcastRefreshStatus = serviceFactory.createGetEndpoint(
  REFRESH_PODCAST_JOB_STATUS_PATH,
)<GetPodcastRefreshStatusArgs, GetPodcastRefreshStatusArgs>();

export const getOwnedPodcasts = serviceFactory.createGetEndpoint(
  OWNED_PODCASTS_PATH,
)<undefined, GetOwnedPodcastsResult>();

export const submitPodpressJob = serviceFactory.createPostEndpoint(
  PODPRESS_JOB_PATH,
)<SubmitPodpressJobArgs, PodpressJobCreationResult>({
  data: identity,
});

/**
 * Note on tracking the progress of a submitted job:
 * Note that the job status and episode status both exist
 *
 * 1. While the job is in a queued status, the individual episodes will
 *    get processed. The individual episode statuses will move from queued
 *    to completed.
 * 2. Once the episodes are all completed, the status should move to a
 *    processing state. Once the job is in a processing state, the progress
 *    percent value will be populated. At this point, the progress percent
 *    can be used.
 * 3. Once the status hits completed, it is safe to assume various
 *    fields like jobOutput.audioUrl and jobOutput.chapters will be
 *    populated.
 */
export const getPodpressJobDetail = serviceFactory.createGetEndpoint(
  PODPRESS_JOB_DETAIL_PATH,
)<GetPodpressJobDetailArgs, PodpressJobDetail>();

/**
 * Get podcast configuration
 *
 * Use podcast_id to retrieve feed's configuration
 */
export const getPodcastConfiguration = serviceFactory.createGetEndpoint(
  PODCAST_CONFIGURATION_PATH,
)<GetPodcastConfigArgs, PodcastConfiguration>();
